import { Link } from '../shell/Link'
import type { View } from './derive'
import { Check } from './RulesMatrix'

/**
 * The first hour after taking over: what to do, in order, and how far along it
 * is. It goes away by itself once every step is done.
 */
export function GettingStarted({ view }: { view: View }): JSX.Element | null {
  const custom = view.groups.filter((g) => g.kind === 'custom').length
  const steps = [
    {
      done: view.machines.length > 0 && view.unclaimed.length === 0,
      title: 'Add your machines',
      text: view.unclaimed.length
        ? `${view.unclaimed.length} ${view.unclaimed.length === 1 ? 'device is' : 'devices are'} already on the network — add them in one press each.`
        : view.machines.length
          ? `${view.machines.length} machines.`
          : 'Add a machine, then join it to the network with the command Kronos gives you.',
      to: 'machines' as const,
      go: 'Open Machines',
    },
    {
      done: custom > 0 || view.machines.length < 12,
      title: 'Group them — when there are many',
      text: 'With a handful of machines, skip this. Later: filter, tick, “New group from these” — a site, its dev robots. “All robots” exists already.',
      to: 'machines' as const,
      go: 'Open Machines',
    },
    {
      done: view.networkOnly.length === 0,
      title: 'Add the people missing from Kronos',
      text: view.networkOnly.length
        ? `${view.networkOnly.map((u) => u.name || u.email).join(', ')} ${view.networkOnly.length === 1 ? 'is' : 'are'} on the network but not in Kronos.`
        : 'Everyone on the network has a Kronos account.',
      to: 'people' as const,
      go: 'Open People',
    },
    {
      done: view.page.world.rules.length > 0,
      title: 'Let people in',
      text: 'Press the square where a person meets a machine below. Teams are for later, when many people need the same machines.',
      to: null,
      go: '',
    },
  ]
  if (steps.every((s) => s.done)) return null
  return (
    <section className="cx-panel cx-panel--padded" aria-label="Getting started">
      <h2 className="cx-panel__title">Getting started</h2>
      <ol className="cx-steps">
        {steps.map((s, i) => (
          <li key={s.title} className={`cx-step ${s.done ? 'is-done' : ''}`}>
            <span className="cx-step__n" aria-hidden="true">{s.done ? <Check /> : i + 1}</span>
            <span className="stack" style={{ gap: 2, flex: 1, minWidth: 0 }}>
              <span className="cx-strong">{s.title}</span>
              <span className="muted cx-small">{s.text}</span>
            </span>
            {!s.done && s.to ? (
              <Link to={{ name: 'connectivity', section: s.to }} className="button">{s.go}</Link>
            ) : null}
          </li>
        ))}
      </ol>
    </section>
  )
}
